import {
  Box,
  Hidden,
  IconButton,
  InputBase,
  Typography
} from "@material-ui/core";
import { graphql } from "gatsby";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import ArrowIcon from "../../components/icons/arrow";
import { formatMultilinesText } from "../../helpers/markdown";
import { useAuthContextSelector } from "../../hooks/use-auth";
import useCountry from "../../hooks/use-country";
import useSubcountry from "../../hooks/use-subcountry";
import useTranslate from "../../hooks/use-translate";
import { subscribeToNewsletter } from "../../services/newsletter";
import { CustomNewsletterField } from "./styles";

export type NewsletterFormDataType = {
  email: string;
};

type Props = Queries.NewsletterSection2FragmentFragment;

export default function NewsletterSection2({
  title,
  subTitle,
  emailInputLabel,
  confirmationText,
  containerBackgroundColor,
  legalTextNode
}: Props) {
  const t = useTranslate();
  const user = useAuthContextSelector(c => c.user);
  const country = useCountry();
  const { subcountry } = useSubcountry();

  const [subscribed, setSubscribed] = useState(false);

  const {
    register,
    handleSubmit,
    errors,
    formState,
    setValue,
    setError,
    watch
  } = useForm<NewsletterFormDataType>();
  const emailValue = watch("email");

  const handleFormSubmit = async (data: NewsletterFormDataType) => {
    try {
      await subscribeToNewsletter(data.email, country, subcountry);
      setSubscribed(true);
    } catch (e) {
      setError("email", { type: "error", message: e?.message });
    }
  };

  const showConfirmation = !!subscribed;

  // After current user is loaded, prefill textfield with email
  useEffect(() => {
    if (user) {
      setValue("email", user.email);
    }
  }, [user]);

  return (
    <form
      onSubmit={handleSubmit(handleFormSubmit)}
      style={{ display: "block" }}
    >
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        textAlign="center"
        paddingY={{ xs: 6, md: 10 }}
        position="relative"
      >
        {/* Title */}
        <Box marginBottom={{ xs: 2, md: 2 }}>
          <Hidden xsDown implementation="css">
            <Typography variant="h4">{title}</Typography>
          </Hidden>
          <Hidden smUp implementation="css">
            <Typography variant="h3">{title}</Typography>
          </Hidden>
        </Box>

        {/* Subtitle */}
        <Box mb={{ xs: 3, md: 4 }}>
          <Typography variant="h5">{subTitle}</Typography>
        </Box>

        {/* Email field */}

        <CustomNewsletterField
          className={formState.errors.email ? "error" : ""}
        >
          <InputBase
            fullWidth
            name="email"
            inputRef={register({
              required: true,
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                message: t("error.invalidEmail")
              }
            })}
            autoComplete="email"
            error={!!errors.email}
            placeholder={emailInputLabel}
            disabled={formState.isSubmitting}
          />
          <IconButton type="submit">
            <ArrowIcon color={!!emailValue ? "secondary" : "inherit"} />
          </IconButton>
        </CustomNewsletterField>

        {/* Legal text */}
        <Box mt={{ xs: 2, md: 2 }} maxWidth="380px">
          <Typography
            variant="caption"
            color="secondary"
            className="white-links"
            dangerouslySetInnerHTML={{
              __html: formatMultilinesText(
                legalTextNode.childMarkdownRemark.html
              )
            }}
          />
        </Box>

        {/* Submit confirmation overlay */}
        {showConfirmation && (
          <Box
            position="absolute"
            top={0}
            left={0}
            width="100%"
            height="100%"
            display="flex"
            justifyContent="center"
            alignItems="center"
            textAlign="center"
            bgcolor={containerBackgroundColor}
          >
            <Typography variant="h4">{confirmationText}</Typography>
          </Box>
        )}
      </Box>
    </form>
  );
}

export const NewsletterSection2Fragment = graphql`
  fragment NewsletterSection2Fragment on DatoCmsCModuleNewsletterSection2 {
    title
    subTitle
    emailInputLabel
    legalTextNode {
      childMarkdownRemark {
        html
      }
    }
    confirmationText
    container
    containerDisableGutters
    containerBackgroundColor
    innerContainer
    model {
      apiKey
    }
  }
`;
